import { reportError } from "@/utils/reportError";
import { fetchIsAdmin } from "@/utils/admin";
import { isLuckyWheelOpen } from "@/constants/challenge";
import { getTodayCH } from "@/utils/date";
import { supabase } from "@/utils/supabase";
import AsyncStorage from "@react-native-async-storage/async-storage";
import * as Haptics from "expo-haptics";
import { LinearGradient } from "expo-linear-gradient";
import { useRouter } from "expo-router";
import { StatusBar } from "expo-status-bar";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Animated,
  Easing,
  KeyboardAvoidingView,
  Platform,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from "react-native";
import ConfettiBurst from '@/components/ConfettiBurst';
import { SafeAreaView } from "react-native-safe-area-context";
import Svg, { Circle, G, Path, Text as SvgText } from "react-native-svg";

/**
 * Onboarding-time welcome wheel. Shown AFTER the 3 explanation steps,
 * BEFORE create-account.
 *
 * Every spin wins BOTH:
 *  - a points bonus (the segment the needle lands on), credited on signup
 *    via the WELCOME_BONUS_PENDING_KEY AsyncStorage flag (unchanged), and
 *  - a GUARANTEED gift: qualification for the prize draw. The user can leave
 *    an email right away (mini form after the spin) — it is stored locally
 *    (PENDING_CONTEST_ENTRY_KEY) and submitted to `contest_entries` as soon
 *    as a session exists (signup or first login), because the project does
 *    NOT allow anonymous sign-ins (external_anonymous_users_enabled=false),
 *    so no DB write is possible before authentication.
 */
export const WELCOME_BONUS_PENDING_KEY = "welcome_bonus_pending_v1";
// Legacy default — used if a stored value can't be parsed as a number.
export const WELCOME_BONUS_FALLBACK_AMOUNT = 50;

/**
 * Participation au tirage capturée AVANT la création du compte.
 * Valeur stockée : JSON { email: string, date: "YYYY-MM-DD" }.
 */
export const PENDING_CONTEST_ENTRY_KEY = "pending_contest_entry_v1";

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

// ---------------------------------------------------------------------------
// Flush des récompenses en attente (bonus points + participation au tirage)
// ---------------------------------------------------------------------------
// Appelé dès qu'une session existe : après le signup si une session est créée
// immédiatement, et à chaque SIGNED_IN/INITIAL_SESSION dans app/_layout.tsx
// (cas nominal : confirmation email obligatoire → pas de session au signup,
// le crédit se fait donc au premier login). Idempotent : les clés sont
// supprimées après succès, et un verrou évite les exécutions concurrentes.

let flushInFlight: Promise<void> | null = null;

export function flushPendingOnboardingRewards(userId: string): Promise<void> {
  if (!flushInFlight) {
    flushInFlight = doFlush(userId).finally(() => {
      flushInFlight = null;
    });
  }
  return flushInFlight;
}

async function doFlush(userId: string): Promise<void> {
  if (!isLuckyWheelOpen()) return;

  // 1) Bonus de bienvenue (points de la roue d'onboarding).
  try {
    const stored = await AsyncStorage.getItem(WELCOME_BONUS_PENDING_KEY);
    if (stored != null) {
      const parsed = parseInt(stored, 10);
      const bonusAmount =
        Number.isFinite(parsed) && parsed > 0 ? parsed : WELCOME_BONUS_FALLBACK_AMOUNT;
      // Crédit via RPC serveur ONE-SHOT (claim_welcome_bonus) : une seule
      // fois par utilisateur (marqueur user_scores.welcome_bonus_at), montant
      // borné côté serveur. L'ancien UPDATE direct était rejouable à volonté
      // (deep link onboarding-wheel après déconnexion → points infinis).
      const { data: claim, error: claimError } = await supabase.rpc(
        "claim_welcome_bonus",
        { p_amount: bonusAmount }
      );
      if (!claimError) {
        // credited OU already_claimed : dans les deux cas la clé locale n'a
        // plus d'utilité (le serveur ne re-créditera jamais).
        await AsyncStorage.removeItem(WELCOME_BONUS_PENDING_KEY);
        void claim;
      }
    }
  } catch (err) {
    reportError(err); // clé conservée → nouvel essai au prochain login
  }

  // 2) Participation au tirage capturée sur la roue d'onboarding.
  try {
    const stored = await AsyncStorage.getItem(PENDING_CONTEST_ENTRY_KEY);
    if (stored != null) {
      let email: string | null = null;
      try {
        const parsed = JSON.parse(stored);
        if (typeof parsed?.email === "string") email = parsed.email.trim().toLowerCase();
      } catch {
        // valeur illisible → on la supprime plus bas
      }
      if (email && EMAIL_RE.test(email)) {
        const { error } = await supabase.from("contest_entries").insert({
          user_id: userId,
          email,
          consent_rgpd: true, // CGU acceptées au signup (comme LuckyWheelModal)
        });
        // 23505 = déjà une participation aujourd'hui → rien à faire.
        if (error && error.code !== "23505") {
          reportError(error);
          return; // clé conservée → nouvel essai au prochain login
        }
      }
      await AsyncStorage.removeItem(PENDING_CONTEST_ENTRY_KEY);
    }
  } catch (err) {
    reportError(err);
  }
}

// ---------------------------------------------------------------------------
// Écran
// ---------------------------------------------------------------------------

// Each segment is a (color, points) pair. The order matters: index 0 is the
// segment between 12 o'clock and ~2 o'clock (upper-right), then clockwise.
// Mix of low and high values keeps a bit of suspense during the spin.
const SEGMENTS: { color: string; amount: number }[] = [
  { color: "#CD7728", amount: 30 },
  { color: "#1C453F", amount: 50 },
  { color: "#78A584", amount: 10 },
  { color: "#CD7728", amount: 20 },
  { color: "#1C453F", amount: 40 },
  { color: "#78A584", amount: 25 },
];
const SEGMENT_COUNT = SEGMENTS.length;

type Step = "ready" | "spinning" | "done" | "form";

export default function OnboardingWheelScreen() {
  const { t } = useTranslation();
  const router = useRouter();
  const spinValue = useRef(new Animated.Value(0)).current;
  const [showConfetti, setShowConfetti] = useState(false);
  const [step, setStep] = useState<Step>("ready");
  const [wonAmount, setWonAmount] = useState<number | null>(null);
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState<string | null>(null);

  const segmentAngle = 360 / SEGMENT_COUNT;

  // Écran réservé au parcours d'INSCRIPTION (aucun compte encore créé) : la
  // roue stocke un bonus en attente (WELCOME_BONUS_PENDING_KEY) crédité au
  // prochain login. Un utilisateur DÉJÀ connecté qui arrive ici par deep link
  // (repas://onboarding-wheel) pourrait donc rejouer la roue et se re-créditer
  // le bonus de bienvenue à chaque redémarrage → on le renvoie dans l'app.
  // Les comptes admin QA (utils/admin.ts) gardent l'accès en prévisualisation.
  // Sens sûr vis-à-vis de l'hydratation web : session null (timing) = pas de
  // redirection, comportement nominal du parcours d'inscription.
  useEffect(() => {
    supabase.auth
      .getSession()
      .then(async ({ data: { session } }) => {
        if (session && !(await fetchIsAdmin(session.user.id))) {
          router.replace("/(tabs)");
        }
      })
      .catch(() => undefined);
  }, [router]);

  const startSpin = () => {
    if (!isLuckyWheelOpen() || step !== "ready") return;
    setStep("spinning");

    const targetSegment = Math.floor(Math.random() * SEGMENT_COUNT);
    const extraSpins = 5;
    const targetAngle = segmentAngle * targetSegment + segmentAngle / 2;
    const totalRotation = extraSpins * 360 + (360 - targetAngle);

    const hapticInterval = setInterval(() => {
      Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light).catch(() => undefined);
    }, 100);

    spinValue.setValue(0);
    Animated.timing(spinValue, {
      toValue: totalRotation,
      duration: 3500,
      easing: Easing.out(Easing.cubic),
      useNativeDriver: true,
    }).start(() => {
      clearInterval(hapticInterval);
      Haptics.notificationAsync(Haptics.NotificationFeedbackType.Success).catch(
        () => undefined
      );
      setShowConfetti(true);
      setWonAmount(SEGMENTS[targetSegment].amount);
      setStep("done");
    });
  };

  const storeWelcomeBonus = async () => {
    try {
      const amount = wonAmount ?? WELCOME_BONUS_FALLBACK_AMOUNT;
      await AsyncStorage.setItem(WELCOME_BONUS_PENDING_KEY, String(amount));
    } catch {
      // Non-blocking — bonus credit is gated by this flag, but losing it
      // just means the user misses the bonus (degraded, not broken).
    }
  };

  // « Continuer » après le spin → mini-formulaire email (cadeau garanti).
  const handleShowForm = () => setStep("form");

  // Valide l'email et stocke la participation en attente, puis → signup.
  const handleSubmitEmail = async () => {
    const normalized = email.trim().toLowerCase();
    if (!EMAIL_RE.test(normalized)) {
      setEmailError(t("onboardingWheel.emailInvalid"));
      return;
    }
    setEmailError(null);
    try {
      await AsyncStorage.setItem(
        PENDING_CONTEST_ENTRY_KEY,
        JSON.stringify({ email: normalized, date: getTodayCH() })
      );
    } catch {
      // Non-blocking : au pire la participation devra être refaite via la
      // roue quotidienne.
    }
    await storeWelcomeBonus();
    router.replace("/create-account");
  };

  // « Plus tard » : pas de participation stockée, on garde juste le bonus.
  const handleSkipForm = async () => {
    await storeWelcomeBonus();
    router.replace("/create-account");
  };

  const rotation = spinValue.interpolate({
    inputRange: [0, 360],
    outputRange: ["0deg", "360deg"],
  });

  const size = 260;
  const center = size / 2;
  const radius = center - 12;

  return (
    <View style={styles.container}>
      <StatusBar style="light" />
      <LinearGradient
        colors={["#FCC189", "#CD7728", "#CD7728"]}
        locations={[0, 0.41, 1]}
        style={styles.background}
      >
        <KeyboardAvoidingView
          behavior={Platform.OS === "ios" ? "padding" : undefined}
          style={styles.background}
        >
          <SafeAreaView style={styles.safeArea}>
            {!isLuckyWheelOpen() ? (
              <View style={styles.content}>
                <View style={styles.header}>
                  <Text style={styles.title} accessibilityRole="header">
                    {t("onboardingWheel.lockedTitle")}
                  </Text>
                  <Text style={styles.subtitle}>
                    {t("onboardingWheel.lockedSubtitle")}
                  </Text>
                </View>
                <TouchableOpacity
                  style={styles.cta}
                  onPress={() => router.replace("/create-account")}
                  activeOpacity={0.85}
                  accessibilityRole="button"
                  accessibilityLabel={t("onboardingWheel.lockedCta")}
                >
                  <LinearGradient
                    colors={["#1C453F", "#78A584"]}
                    start={{ x: 0, y: 0 }}
                    end={{ x: 1, y: 0 }}
                    style={styles.ctaGradient}
                  >
                    <Text style={styles.ctaText}>{t("onboardingWheel.lockedCta")}</Text>
                  </LinearGradient>
                </TouchableOpacity>
              </View>
            ) : step === "form" ? (
              <ScrollView
                contentContainerStyle={styles.formContent}
                keyboardShouldPersistTaps="handled"
                showsVerticalScrollIndicator={false}
              >
                <View style={styles.header}>
                  <Text style={styles.title} accessibilityRole="header">
                    {t("onboardingWheel.formTitle")}
                  </Text>
                  <Text style={styles.subtitle}>
                    {t("onboardingWheel.formSubtitle")}
                  </Text>
                </View>

                <View style={styles.formCard}>
                  <Text style={styles.label} nativeID="onboarding-email-label">
                    {t("onboardingWheel.emailLabel")}
                  </Text>
                  <TextInput
                    style={styles.input}
                    value={email}
                    onChangeText={setEmail}
                    placeholder={t("onboardingWheel.emailPlaceholder")}
                    placeholderTextColor="#8A8A8A"
                    keyboardType="email-address"
                    autoCapitalize="none"
                    autoCorrect={false}
                    autoComplete="email"
                    textContentType="emailAddress"
                    inputMode="email"
                    returnKeyType="done"
                    onSubmitEditing={handleSubmitEmail}
                    accessibilityLabel={t("onboardingWheel.emailAccessibilityLabel")}
                    accessibilityLabelledBy="onboarding-email-label"
                    maxLength={254}
                  />
                  {emailError ? (
                    <Text
                      style={styles.errorText}
                      accessibilityLiveRegion="assertive"
                      accessibilityRole="alert"
                    >
                      {emailError}
                    </Text>
                  ) : null}
                  <Text style={styles.formHint}>
                    {t("onboardingWheel.formHint")}
                  </Text>
                </View>

                <View style={styles.formButtons}>
                  <TouchableOpacity
                    style={styles.cta}
                    onPress={handleSubmitEmail}
                    activeOpacity={0.85}
                    accessibilityRole="button"
                    accessibilityLabel={t("onboardingWheel.submitButton")}
                  >
                    <LinearGradient
                      colors={["#1C453F", "#78A584"]}
                      start={{ x: 0, y: 0 }}
                      end={{ x: 1, y: 0 }}
                      style={styles.ctaGradient}
                    >
                      <Text style={styles.ctaText}>{t("onboardingWheel.submitButton")}</Text>
                    </LinearGradient>
                  </TouchableOpacity>
                  <TouchableOpacity
                    style={styles.skipButton}
                    onPress={handleSkipForm}
                    activeOpacity={0.7}
                    accessibilityRole="button"
                    accessibilityLabel={t("common.later")}
                  >
                    <Text style={styles.skipText}>{t("common.later")}</Text>
                  </TouchableOpacity>
                </View>
              </ScrollView>
            ) : (
              <View style={styles.content}>
                <View style={styles.header}>
                  <Text style={styles.title} accessibilityRole="header">
                    {step === "done"
                      ? t("onboardingWheel.doneTitle")
                      : t("onboardingWheel.readyTitle")}
                  </Text>
                  <Text style={styles.subtitle}>
                    {step === "done" && wonAmount != null
                      ? t("onboardingWheel.doneSubtitle", { amount: wonAmount })
                      : t("onboardingWheel.readySubtitle")}
                  </Text>
                </View>

                <View style={styles.wheelContainer}>
                  <View style={styles.pointer} pointerEvents="none">
                    <View style={styles.pointerTriangle} />
                  </View>

                  <Animated.View style={{ transform: [{ rotate: rotation }] }}>
                    <Svg width={size} height={size}>
                      <G>
                        {SEGMENTS.map(({ color, amount }, index) => {
                          const startAngle = (index * segmentAngle - 90) * (Math.PI / 180);
                          const endAngle = ((index + 1) * segmentAngle - 90) * (Math.PI / 180);
                          const x1 = center + radius * Math.cos(startAngle);
                          const y1 = center + radius * Math.sin(startAngle);
                          const x2 = center + radius * Math.cos(endAngle);
                          const y2 = center + radius * Math.sin(endAngle);
                          const largeArc = segmentAngle > 180 ? 1 : 0;
                          const pathData = `M ${center} ${center} L ${x1} ${y1} A ${radius} ${radius} 0 ${largeArc} 1 ${x2} ${y2} Z`;
                          const labelAngle = (index * segmentAngle + segmentAngle / 2 - 90) * (Math.PI / 180);
                          const labelRadius = radius * 0.68;
                          const labelX = center + labelRadius * Math.cos(labelAngle);
                          const labelY = center + labelRadius * Math.sin(labelAngle);
                          const labelFontSize = 18;
                          return (
                            <G key={index}>
                              <Path
                                d={pathData}
                                fill={color}
                                stroke="#FFF"
                                strokeWidth={3}
                              />
                              <SvgText
                                x={labelX}
                                y={labelY + labelFontSize / 3}
                                fill="#FFF"
                                fontSize={labelFontSize}
                                fontFamily="Inter_700Bold"
                                textAnchor="middle"
                              >
                                {amount}
                              </SvgText>
                            </G>
                          );
                        })}
                        <Circle cx={center} cy={center} r={34} fill="#FFF" />
                        <Circle cx={center} cy={center} r={28} fill="#FBEFE3" />
                        {/* Cadeau garanti quel que soit le segment. */}
                        <SvgText
                          x={center}
                          y={center + 8}
                          fontSize={22}
                          textAnchor="middle"
                        >
                          🎁
                        </SvgText>
                      </G>
                    </Svg>
                  </Animated.View>
                </View>

                {step === "done" && wonAmount != null && (
                  <View style={styles.bonusColumn}>
                    <View style={styles.bonusPill}>
                      <Text style={styles.bonusPillText}>
                        {t("onboardingWheel.bonusPointsPill", { amount: wonAmount })}
                      </Text>
                    </View>
                    <View style={styles.bonusPill}>
                      <Text style={styles.bonusPillText}>
                        {t("onboardingWheel.bonusEntryPill")}
                      </Text>
                    </View>
                  </View>
                )}

                <TouchableOpacity
                  style={styles.cta}
                  onPress={step === "done" ? handleShowForm : startSpin}
                  disabled={step === "spinning"}
                  activeOpacity={0.85}
                  accessibilityRole="button"
                  accessibilityLabel={
                    step === "done" ? t("common.continue") : t("onboardingWheel.spinCta")
                  }
                >
                  <LinearGradient
                    colors={["#1C453F", "#78A584"]}
                    start={{ x: 0, y: 0 }}
                    end={{ x: 1, y: 0 }}
                    style={[styles.ctaGradient, step === "spinning" && styles.ctaDisabled]}
                  >
                    <Text style={styles.ctaText}>
                      {step === "spinning"
                        ? t("onboardingWheel.spinning")
                        : step === "done"
                        ? t("common.continue")
                        : t("onboardingWheel.spinCta")}
                    </Text>
                  </LinearGradient>
                </TouchableOpacity>
              </View>
            )}
          </SafeAreaView>
        </KeyboardAvoidingView>

        {showConfetti && <ConfettiBurst />}
      </LinearGradient>
    </View>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1 },
  background: { flex: 1 },
  safeArea: { flex: 1 },
  content: {
    flex: 1,
    paddingHorizontal: 24,
    paddingTop: 24,
    paddingBottom: 40,
    justifyContent: "space-between",
    alignItems: "center",
  },
  formContent: {
    flexGrow: 1,
    paddingHorizontal: 24,
    paddingTop: 24,
    paddingBottom: 40,
    justifyContent: "center",
  },
  header: { alignItems: "center", marginTop: 16 },
  title: {
    fontFamily: "Inter_700Bold",
    fontSize: 28,
    color: "#FFFFFF",
    textAlign: "center",
    marginBottom: 8,
  },
  subtitle: {
    fontFamily: "Inter_400Regular",
    fontSize: 15,
    color: "#FFFFFF",
    opacity: 0.92,
    textAlign: "center",
    lineHeight: 22,
    paddingHorizontal: 16,
  },
  wheelContainer: {
    width: 260,
    height: 260,
    alignItems: "center",
    justifyContent: "center",
  },
  pointer: {
    position: "absolute",
    top: -8,
    zIndex: 10,
    alignItems: "center",
  },
  pointerTriangle: {
    width: 0,
    height: 0,
    borderLeftWidth: 12,
    borderRightWidth: 12,
    borderTopWidth: 22,
    borderLeftColor: "transparent",
    borderRightColor: "transparent",
    borderTopColor: "#733801",
  },
  bonusColumn: {
    alignItems: "center",
    gap: 8,
    marginBottom: 8,
  },
  bonusPill: {
    backgroundColor: "rgba(255, 255, 255, 0.95)",
    borderRadius: 999,
    paddingHorizontal: 18,
    paddingVertical: 10,
  },
  bonusPillText: {
    fontFamily: "Inter_700Bold",
    fontSize: 16,
    color: "#CD7728",
  },
  formCard: {
    backgroundColor: "#FFFFFF",
    borderRadius: 24,
    padding: 24,
    marginTop: 24,
    width: "100%",
  },
  label: {
    fontFamily: "Inter_600SemiBold",
    fontSize: 14,
    color: "#222",
    marginBottom: 8,
  },
  input: {
    fontFamily: "Inter_400Regular",
    fontSize: 17,
    color: "#000",
    borderWidth: 1.5,
    borderColor: "#BBB",
    borderRadius: 12,
    paddingHorizontal: 14,
    minHeight: 52,
    paddingVertical: 14,
    backgroundColor: "#FFF",
  },
  errorText: {
    fontFamily: "Inter_600SemiBold",
    fontSize: 14,
    color: "#B0291C",
    marginTop: 10,
    textAlign: "center",
  },
  formHint: {
    fontFamily: "Inter_400Regular",
    fontSize: 12,
    color: "#666",
    marginTop: 12,
    lineHeight: 17,
  },
  formButtons: {
    marginTop: 24,
    gap: 4,
  },
  cta: {
    width: "100%",
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.25,
    shadowRadius: 3.84,
    elevation: 5,
  },
  ctaGradient: {
    paddingVertical: 16,
    borderRadius: 12,
    alignItems: "center",
  },
  ctaDisabled: { opacity: 0.7 },
  ctaText: {
    fontFamily: "Inter_700Bold",
    fontSize: 16,
    color: "#FFFFFF",
  },
  skipButton: {
    paddingVertical: 14,
    alignItems: "center",
  },
  skipText: {
    fontFamily: "Inter_600SemiBold",
    fontSize: 15,
    color: "#FFFFFF",
    opacity: 0.9,
    textDecorationLine: "underline",
  },
});
